// 全局状态：家人、照片、结果。模块级 store + 订阅，组件用 useStore() 读取。

import { useEffect, useState } from "preact/hooks";
import { COLORS, type StyleRequest } from "../shared/styles";
import type { BeautyLevel } from "./lib/beauty";
import type { Member, Photo, Result } from "./lib/db";
import * as db from "./lib/db";
import { forgetPhoto, forgetResult } from "./lib/render";

interface State {
  ready: boolean;
  members: Member[];
  photos: Record<string, Photo[]>;
  results: Record<string, Result[]>;
  beauty: BeautyLevel;
  colorId: string;
}

let state: State = {
  ready: false,
  members: [],
  photos: {},
  results: {},
  beauty: (Number(localStorage.getItem("jq.beauty") ?? 1) as BeautyLevel) || 0,
  colorId: localStorage.getItem("jq.color") ?? COLORS[0].id,
};
if (![0, 1, 2].includes(state.beauty)) state.beauty = 1;

const subs = new Set<() => void>();

function set(patch: Partial<State>) {
  state = { ...state, ...patch };
  for (const s of subs) s();
}

export function getState() {
  return state;
}

export function useStore<T>(select: (s: State) => T): T {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    subs.add(fn);
    return () => {
      subs.delete(fn);
    };
  }, []);
  return select(state);
}

export async function init() {
  const members = (await db.getAll<Member>("members")).sort((a, b) => a.createdAt - b.createdAt);
  set({ members, ready: true });
}

export function setBeauty(level: BeautyLevel) {
  localStorage.setItem("jq.beauty", String(level));
  set({ beauty: level });
}

export function setColor(colorId: string) {
  localStorage.setItem("jq.color", colorId);
  set({ colorId });
}

// ---------- 家人 ----------

export async function saveMember(m: Member) {
  await db.put("members", m);
  const exists = state.members.some((x) => x.id === m.id);
  set({ members: exists ? state.members.map((x) => (x.id === m.id ? m : x)) : [...state.members, m] });
}

export async function removeMember(id: string) {
  for (const p of state.photos[id] ?? []) forgetPhoto(p.id);
  await db.deleteMember(id);
  const photos = { ...state.photos };
  delete photos[id];
  set({ members: state.members.filter((m) => m.id !== id), photos });
}

// ---------- 照片 ----------

export async function loadPhotos(memberId: string) {
  const list = (await db.byIndex<Photo>("photos", "memberId", memberId)).sort((a, b) => a.createdAt - b.createdAt);
  set({ photos: { ...state.photos, [memberId]: list } });
  return list;
}

export function addPhotoLocal(p: Photo) {
  const list = [...(state.photos[p.memberId] ?? []), p];
  set({ photos: { ...state.photos, [p.memberId]: list } });
}

export async function removePhoto(p: Photo) {
  await db.deletePhoto(p);
  forgetPhoto(p.id);
  const list = (state.photos[p.memberId] ?? []).filter((x) => x.id !== p.id);
  const results = { ...state.results };
  delete results[p.id];
  set({ photos: { ...state.photos, [p.memberId]: list }, results });
}

// ---------- 结果 ----------

export async function loadResults(photoId: string) {
  const list = await db.byIndex<Result>("results", "photoId", photoId);
  set({ results: { ...state.results, [photoId]: list } });
  return list;
}

export async function refreshResult(id: string, photoId: string) {
  const r = await db.get<Result>("results", id);
  if (!r) return;
  forgetResult(id);
  const list = (state.results[photoId] ?? []).filter((x) => x.id !== id);
  set({ results: { ...state.results, [photoId]: [...list, r] } });
}

export async function toggleFavorite(r: Result) {
  const next = { ...r, favorite: !r.favorite };
  await db.put("results", next);
  const list = (state.results[r.photoId] ?? []).map((x) => (x.id === r.id ? next : x));
  set({ results: { ...state.results, [r.photoId]: list } });
}

export async function removeResult(r: Result) {
  await db.del("results", r.id);
  forgetResult(r.id);
  const list = (state.results[r.photoId] ?? []).filter((x) => x.id !== r.id);
  set({ results: { ...state.results, [r.photoId]: list } });
}

export async function clearAllResults() {
  await db.clearResults();
  set({ results: {} });
}

export function findResult(photoId: string, id: string): Result | undefined {
  return state.results[photoId]?.find((r) => r.id === id);
}

const thumbUrls = new Map<string, { blob: Blob; url: string }>();
export function thumbUrl(r: Result): string {
  const hit = thumbUrls.get(r.id);
  if (hit && hit.blob === r.thumb) return hit.url;
  if (hit) URL.revokeObjectURL(hit.url);
  const url = URL.createObjectURL(r.thumb);
  thumbUrls.set(r.id, { blob: r.thumb, url });
  return url;
}

const photoUrls = new Map<string, string>();
export function photoUrl(p: Photo): string {
  let u = photoUrls.get(p.id);
  if (!u) {
    u = URL.createObjectURL(p.base);
    photoUrls.set(p.id, u);
  }
  return u;
}

export type { StyleRequest };
